import { generateGraphNodePathMap } from 'components/scenarios/utils/graph/metadata'
import {
  isGraphBranch,
  isGraphNode,
  isInfraAttackNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { serializeNodePathToId } from 'components/scenarios/utils/graph/serialization'
import {
  type GraphNode,
  type GraphNodeList,
  type InfraAttackNode,
  type ScenarioGraph,
  ScenarioGraphNodeType,
  ScenarioLifecycle
} from 'types/api/scenarios/scenario'
import { naturalSortBy } from 'utils/list'
import { isObject } from 'utils/obj'

import type {
  GraphNodeAtPath,
  GraphNodePath
} from 'components/scenarios/utils/graph/types'
import type { ScenarioRunGraph } from 'types/api/scenarios/runs/scenario-runs'

/**
 * Follow the supplied GraphPath to extract a node from a ScenarioGraph
 */
export function getNodeAtPath(
  graph: ScenarioGraph,
  path?: GraphNodePath
): GraphNodeAtPath {
  if (!path || path.length === 0) {
    return null
  }

  // short circuit to avoid infinite loop in parent path retrieval
  if (path.length === 1 && path[0] === 'nodes') {
    return graph.nodes as unknown as ScenarioGraph
  }

  let node = graph

  path.forEach((pathPart) => {
    node = node[pathPart]
  })

  // nodes in the graph are of various types
  // so we whittle down the exact type here

  // this is an actual graph node (attack, status check, delay, concurrent node)
  if (isGraphNode(node)) {
    return node as unknown as GraphNode
  }

  // branch list
  if (Array.isArray(node)) {
    return node as unknown as ScenarioGraph[]
  }

  if (isObject(node)) {
    // an individual branch is a ScenarioGraph
    if (isGraphBranch(getParentGraphNode(graph, getNodeParentPath(path)))) {
      return node as unknown as ScenarioGraph
    }

    // otherwise and object is a list of nodes
    return node as unknown as GraphNodeList
  }

  return node
}

/**
 * Typing here is explicitly vague because it can accept either a branch or a node
 */
export function getParentGraphNode(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  let parentPath = getNodeParentPath(nodePath)
  let parentNode = getNodeAtPath(graph, parentPath)

  while (!isGraphNode(parentNode)) {
    parentPath = getNodeParentPath(parentPath)
    parentNode = getNodeAtPath(graph, parentPath)
  }

  return parentNode
}

export function findNearestAttackNode(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  // grab the map for actual node paths
  const graphNodePathMap = generateGraphNodePathMap(graph)

  const attackNodes = Object.entries(graphNodePathMap)
    // filter out anything that's not an attack node
    .filter(([, path]) => {
      const node = getNodeAtPath(graph, path)
      return isInfraAttackNode(node)
    })
    // pull in the actual node, and ensure it has a valid `id`
    .map(([, path]) => {
      const node: GraphNode = getNodeAtPath(graph, path) as GraphNode
      node.id = serializeNodePathToId(path)
      return node
    })

  // no attack nodes?
  if (attackNodes.length < 1) {
    return null
  }

  // if there's only one, return it
  if (nodePath.length === 1) {
    return attackNodes[0]
  }

  // ensure the list is sorted by id (which should give us the graph order from the top down)
  // so whatever we return is the nearest attack above the target in the graph
  const sortedAttackNodes = naturalSortBy(attackNodes, 'id', 'asc')

  // now find the nearest one
  // let's compare the ids to find the nearest one
  const targetNodeId = serializeNodePathToId(nodePath)
  const targetNodeIdParts = targetNodeId.split('-')

  // compare the node ids to generate a "distance"
  // split the target node id into a list of its parts
  // then compare those to the corresponding parts in each attack node
  // the attack node with the smallest distance (fewer differences in path) is the nearest
  const nodesWidthIdDistance = sortedAttackNodes.map((node) => {
    const nodeIdParts = node.id.split('-')
    let distance = 0
    for (let i = 0; i < targetNodeIdParts.length; i++) {
      if (nodeIdParts[i] !== targetNodeIdParts[i]) {
        distance++
      }
    }

    return {
      node,
      distance
    }
  })

  // re-sort the list and return the first attack
  const sortedNodes = naturalSortBy(nodesWidthIdDistance, 'distance', 'asc')
  return sortedNodes[0].node
}

export function getOrderedNodesFromBranch(
  branch: ScenarioGraph | ScenarioRunGraph
) {
  const orderedNodes: Array<[key: string | number, node: GraphNode]> = []
  let nextNode = branch.nodes[branch.start_id]

  while (nextNode) {
    orderedNodes.push([nextNode.id, nextNode])

    nextNode = branch.nodes[nextNode.next]
  }

  return orderedNodes
}

/**
 * Return the non-branch nodes that match the condition set in `matchFunc` (or all if not provided).
 */
export function getMatchingGraphNodes<T extends GraphNode = GraphNode>(
  nodes?: GraphNodeList,
  matchingNodes: T[] = [],
  matchFunc?: (node: GraphNode) => boolean
) {
  Object.values(nodes ?? {}).forEach((node) => {
    if (node.type === ScenarioGraphNodeType.Concurrent) {
      node.branches.forEach((branch) => {
        return getMatchingGraphNodes<T>(branch.nodes, matchingNodes, matchFunc)
      })
    } else if (!matchFunc || matchFunc(node)) {
      matchingNodes.push(node as T)
    }
  })

  return matchingNodes
}

/**
 * Recursively extract active nodes (`ScenarioLifecycle.Active`)
 */
export function getActiveGraphNodes(nodes?: GraphNodeList) {
  return getMatchingGraphNodes(
    nodes,
    undefined,
    (node) => node.state?.lifecycle === ScenarioLifecycle.Active
  )
}

/**
 * Get the node from the graph by its node path, but return it typed as an InfraAttackNode
 * This is used as a type guard since getNodeAtPath can return various types
 */
export function getGraphNodeAtPathAsInfraAttack(
  graph: ScenarioGraph,
  path?: GraphNodePath
): InfraAttackNode {
  const node = getNodeAtPath(graph, path)

  if (!isInfraAttackNode(node)) {
    return {} as InfraAttackNode
  }

  return node as InfraAttackNode
}

export function getNodeParentPath(path: GraphNodePath): GraphNodePath {
  return path.slice(0, -1) as GraphNodePath
}
